/**
 * Parse BankIncomeAudit.calculationTransactions into clickable derivation steps
 * for the admin Income tab explainability strip.
 *
 * Newer audits include iqrCalculation / magnitudeCalculation / normalization.
 * Older snapshots only have calculationValueTrace + stabilityCvFromIqrAdjustedSeries —
 * those are reconstructed client-side with the same IQR / magnitude / frequency rules.
 */

export type IncomeCalcTraceEntry = {
  valueUsedInCalculation: number;
  originalPayAmount: number;
  replaced: boolean;
  adjustmentKind: 'none' | 'high_spike_adjusted' | string;
  extraAmountRemoved?: number;
};

export type IncomeCalcPaySlot = {
  index: number;
  amount: number;
  depositDate: string | null;
  payorRaw: string | null;
};

export type IncomeCalcStream = {
  payor: string;
  payorType?: string;
  recurrenceEvents?: number;
  monthlyAmountNormalized: number;
  /** True when iqr/magnitude/normalization were rebuilt from a legacy snapshot. */
  reconstructed?: boolean;
  /** Chronological pay slots when rawTransactions present (for dated key/value rows). */
  paySlots?: IncomeCalcPaySlot[];
  calculationValueTrace: IncomeCalcTraceEntry[];
  iqrCalculation: {
    threshold: number | null;
    smoothing: {
      enabled: boolean;
      window: number;
      minLength: number;
      applied: boolean;
    } | null;
    boundsFrom: 'raw' | 'smoothed' | string | null;
    q1: number | null;
    q3: number | null;
    iqr: number | null;
    lowerBound: number | null;
    upperBound: number | null;
    medianInBand: number | null;
    highSpikeAmounts: number[];
    extraAmountRemoved: number[];
    rawPayAmounts: number[];
    smoothedValues: number[] | null;
    payAmountsUsed: number[];
  };
  magnitudeCalculation: {
    magnitudeMethod: 'mean' | 'mode' | 'recency' | string;
    magnitude: number;
    modeValue: number;
    modeRatio: number;
    seriesMean: number;
    recency: {
      evaluated: boolean;
      lastThreeMean: number | null;
      recentModeRatio: number | null;
      hikeGatePassed: boolean;
    } | null;
    rulePath: string;
  };
  normalization: {
    incomeClass?: string;
    frequency: string;
    basis?: string;
    basisAmount: number;
    monthlyFactor: number;
    monthlyAmount: number;
    formula: string;
    daysInMonth?: number;
    averageGapDays?: number;
    effectiveGapDays?: number;
  };
};

export type IncomeCalculationTransactions = {
  schemaVersion?: number;
  capturedAt?: string;
  bankVerificationId?: string;
  note?: string;
  depositRollup?: {
    totalIngestRows: number;
    eligibleCount: number;
    ineligibleCount: number;
  };
  streams?: IncomeCalcStream[];
};

export type DerivationStepId =
  | 'identified'
  | 'rolling_median'
  | 'quantiles'
  | 'fence'
  | 'adjusted'
  | 'magnitude'
  | 'normalize';

export type DerivationStep = {
  id: DerivationStepId;
  label: string;
  shortResult: string;
  skipped?: boolean;
  skipReason?: string;
};

export type DerivationViewModel = {
  payor: string;
  monthlyAmount: number;
  steps: DerivationStep[];
  stream: IncomeCalcStream;
};

export type ParseCalcOptions = {
  /** From IncomeDashboardDetail.metrics.frequencyClassification when audit omits normalization. */
  frequencyHint?: string | null;
};

function asNumberArray(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
}

function asNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const cleaned = v.replace(/,/g, '').trim();
    if (!cleaned) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseDepositDate(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return new Date(v).toISOString();
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString();
  return null;
}

const PAY_SLOT_AMOUNT_EPS = 0.01;

/**
 * Map deposit-level rawTransactions onto calculation pay amounts.
 * Merges consecutive split-payroll credits (e.g. base + bonus) into one dated slot.
 */
function alignPaySlotsToAmounts(
  transactions: IncomeCalcPaySlot[],
  targetAmounts: number[],
): IncomeCalcPaySlot[] {
  if (targetAmounts.length === 0) return [];
  if (transactions.length === 0) {
    return targetAmounts.map((amount, index) => ({
      index,
      amount,
      depositDate: null,
      payorRaw: null,
    }));
  }

  if (transactions.length === targetAmounts.length) {
    const amountsMatch = transactions.every(
      (slot, i) => Math.abs(slot.amount - targetAmounts[i]!) <= PAY_SLOT_AMOUNT_EPS,
    );
    if (amountsMatch) {
      return transactions.map((slot, index) => ({ ...slot, index }));
    }
  }

  const aligned: IncomeCalcPaySlot[] = [];
  let txIdx = 0;

  for (let targetIdx = 0; targetIdx < targetAmounts.length; targetIdx += 1) {
    const target = targetAmounts[targetIdx]!;
    let consumed = 0;
    let depositDate: string | null = null;
    let payorRaw: string | null = null;
    const startTxIdx = txIdx;

    while (txIdx < transactions.length && consumed + PAY_SLOT_AMOUNT_EPS < target) {
      const tx = transactions[txIdx]!;
      if (depositDate == null) depositDate = tx.depositDate;
      if (payorRaw == null) payorRaw = tx.payorRaw;
      consumed += tx.amount;
      txIdx += 1;
    }

    if (txIdx > startTxIdx && Math.abs(consumed - target) <= PAY_SLOT_AMOUNT_EPS) {
      aligned.push({ index: targetIdx, amount: target, depositDate, payorRaw });
      continue;
    }

    if (txIdx > startTxIdx) {
      txIdx = startTxIdx;
    }

    const direct = transactions[targetIdx];
    aligned.push({
      index: targetIdx,
      amount: target,
      depositDate: direct?.depositDate ?? null,
      payorRaw: direct?.payorRaw ?? null,
    });
    if (direct && Math.abs(direct.amount - target) <= PAY_SLOT_AMOUNT_EPS) {
      txIdx = Math.max(txIdx, targetIdx + 1);
    }
  }

  return aligned;
}

function asStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function money(n: number | null | undefined, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(n);
}

export function formatCalcMoney(n: number | null | undefined, digits = 2): string {
  return money(n, digits);
}

export function formatPct(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/** Trailing rolling median — mirrors svc-income-verification math.utils. */
export function rollingMedianSameLength(values: number[], windowSize: number): number[] {
  if (values.length === 0 || windowSize <= 0) return [];
  if (windowSize === 1) return [...values];
  const results: number[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const start = Math.max(0, i - windowSize + 1);
    const window = values.slice(start, i + 1).slice().sort((a, b) => a - b);
    const mid = Math.floor(window.length / 2);
    results.push(window.length % 2 === 0 ? (window[mid - 1]! + window[mid]!) / 2 : window[mid]!);
  }
  return results;
}

function medianOf(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

/** Mirrors income-verification percentile() used for Q1/Q3. */
export function incomePercentile(sortedAsc: number[], p: number): number {
  const n = sortedAsc.length;
  if (n === 0) return 0;
  if (n === 1) return sortedAsc[0]!;
  if (n <= 10) {
    const index = Math.floor((p / 100) * (n - 1));
    return sortedAsc[index]!;
  }
  const index = (p / 100) * (n - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  if (upper >= n) return sortedAsc[n - 1]!;
  return sortedAsc[lower]! * (1 - weight) + sortedAsc[upper]! * weight;
}

export function computeIqrFence(
  boundsSource: number[],
  threshold = 1.5,
): {
  q1: number;
  q3: number;
  iqr: number;
  lowerBound: number;
  upperBound: number;
} | null {
  if (boundsSource.length <= 2) return null;
  const sorted = [...boundsSource].sort((a, b) => a - b);
  const q1 = incomePercentile(sorted, 25);
  const q3 = incomePercentile(sorted, 75);
  const iqr = q3 - q1;
  return {
    q1,
    q3,
    iqr,
    lowerBound: q1 - threshold * iqr,
    upperBound: q3 + threshold * iqr,
  };
}

function modeNear(values: number[]): { modeValue: number; modeRatio: number } {
  if (values.length === 0) return { modeValue: 0, modeRatio: 0 };
  const rounded = values.map((v) => Math.round(v * 100) / 100);
  const counts = new Map<number, number>();
  for (const v of rounded) counts.set(v, (counts.get(v) ?? 0) + 1);
  let modeValue = rounded[0]!;
  let best = 0;
  for (const [v, c] of counts) {
    if (c > best) {
      best = c;
      modeValue = v;
    }
  }
  const modeMatchCount = values.filter((v) => Math.abs(v - modeValue) < 5).length;
  return { modeRatio: modeMatchCount / values.length, modeValue };
}

function seriesMean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** Mirrors ClassificationService.resolveMagnitudeDetails on payAmountsUsed. */
export function resolveMagnitudeFromSeries(values: number[]): IncomeCalcStream['magnitudeCalculation'] {
  const mean = seriesMean(values);
  const { modeRatio, modeValue } = modeNear(values);
  const emptyRecency = {
    evaluated: false,
    lastThreeMean: null as number | null,
    recentModeRatio: null as number | null,
    hikeGatePassed: false,
  };

  if (values.length < 2) {
    return {
      magnitudeMethod: 'mean',
      magnitude: mean,
      modeValue,
      modeRatio,
      seriesMean: mean,
      recency: emptyRecency,
      rulePath: 'payAmountsUsed.length < 2 → series mean',
    };
  }

  if (modeRatio >= 0.6) {
    return {
      magnitudeMethod: 'mode',
      magnitude: modeValue,
      modeValue,
      modeRatio,
      seriesMean: mean,
      recency: emptyRecency,
      rulePath: 'modeRatio >= 0.6 → mode anchoring',
    };
  }

  const lastThree = values.slice(-3);
  const lastThreeMean = seriesMean(lastThree);
  const hikeGatePassed = values.length >= 4 && lastThreeMean > mean * 1.05;

  if (hikeGatePassed) {
    const { modeRatio: recentModeRatio } = modeNear(lastThree);
    const recency = {
      evaluated: true,
      lastThreeMean,
      recentModeRatio,
      hikeGatePassed: true,
    };
    if (recentModeRatio >= 2 / 3) {
      return {
        magnitudeMethod: 'recency',
        magnitude: lastThreeMean,
        modeValue,
        modeRatio,
        seriesMean: mean,
        recency,
        rulePath:
          'modeRatio < 0.6 AND last3Mean > mean×1.05 AND recentModeRatio >= 2/3 → recency',
      };
    }
    return {
      magnitudeMethod: 'mean',
      magnitude: mean,
      modeValue,
      modeRatio,
      seriesMean: mean,
      recency,
      rulePath: 'modeRatio < 0.6 AND hike gate passed but recentModeRatio < 2/3 → series mean',
    };
  }

  return {
    magnitudeMethod: 'mean',
    magnitude: mean,
    modeValue,
    modeRatio,
    seriesMean: mean,
    recency: {
      evaluated: values.length >= 4,
      lastThreeMean: values.length >= 3 ? lastThreeMean : null,
      recentModeRatio: null,
      hikeGatePassed: false,
    },
    rulePath: 'modeRatio < 0.6 and no recency hike → series mean',
  };
}

const FREQUENCY_FACTORS: Record<string, { factor: number; formula: string }> = {
  WEEKLY: { factor: 52 / 12, formula: 'magnitude × (52/12)' },
  BIWEEKLY: { factor: 26 / 12, formula: 'magnitude × (26/12)' },
  SEMI_MONTHLY: { factor: 24 / 12, formula: 'magnitude × (24/12)' },
  SEMIMONTHLY: { factor: 24 / 12, formula: 'magnitude × (24/12)' },
  MONTHLY: { factor: 1, formula: 'magnitude × 1' },
  IRREGULAR: { factor: 1, formula: 'magnitude (IRREGULAR — no frequency multiply)' },
};

function normalizeFrequency(raw: string | null | undefined): string {
  const u = (raw ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!u) return 'UNKNOWN';
  if (u.includes('SEMI')) return 'SEMI_MONTHLY';
  if (u.includes('BIWEEK')) return 'BIWEEKLY';
  if (u.includes('WEEK')) return 'WEEKLY';
  if (u.includes('MONTH') && !u.includes('SEMI')) return 'MONTHLY';
  if (u.includes('IRREGULAR')) return 'IRREGULAR';
  return u;
}

function buildNormalization(
  monthlyAmount: number,
  magnitude: number,
  frequencyHint: string | null | undefined,
): IncomeCalcStream['normalization'] {
  const frequency = normalizeFrequency(frequencyHint);
  const known = FREQUENCY_FACTORS[frequency];
  if (known) {
    return {
      frequency,
      basis: 'magnitude',
      basisAmount: magnitude,
      monthlyFactor: known.factor,
      monthlyAmount,
      formula: known.formula,
    };
  }
  const factor = magnitude > 0 ? monthlyAmount / magnitude : 1;
  return {
    frequency: frequency || 'UNKNOWN',
    basis: 'magnitude',
    basisAmount: magnitude,
    monthlyFactor: factor,
    monthlyAmount,
    formula: 'monthlyAmount ≈ magnitude × inferred factor (frequency missing on audit)',
  };
}

function parseTrace(raw: unknown): IncomeCalcTraceEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row): IncomeCalcTraceEntry | null => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const valueUsedInCalculation = asNum(o.valueUsedInCalculation);
      const originalPayAmount = asNum(o.originalPayAmount);
      if (valueUsedInCalculation == null || originalPayAmount == null) return null;
      const entry: IncomeCalcTraceEntry = {
        valueUsedInCalculation,
        originalPayAmount,
        replaced: Boolean(o.replaced),
        adjustmentKind: asStr(o.adjustmentKind) || 'none',
      };
      const extraAmountRemoved = asNum(o.extraAmountRemoved);
      if (extraAmountRemoved != null) entry.extraAmountRemoved = extraAmountRemoved;
      return entry;
    })
    .filter((x): x is IncomeCalcTraceEntry => x != null);
}

function parseIdentifiedPaysSlots(
  identifiedPaysRaw: unknown,
  fallbackAmounts: number[],
): IncomeCalcPaySlot[] | null {
  if (!Array.isArray(identifiedPaysRaw) || identifiedPaysRaw.length !== fallbackAmounts.length) {
    return null;
  }
  const slots: IncomeCalcPaySlot[] = [];
  for (let index = 0; index < fallbackAmounts.length; index += 1) {
    const row = identifiedPaysRaw[index];
    if (!row || typeof row !== 'object') return null;
    const o = row as Record<string, unknown>;
    const amount = asNum(o.amount);
    const target = fallbackAmounts[index]!;
    if (amount == null || Math.abs(amount - target) > PAY_SLOT_AMOUNT_EPS) {
      return null;
    }
    slots.push({
      index,
      amount: target,
      depositDate:
        parseDepositDate(o.depositDate) ??
        parseDepositDate(o.date) ??
        parseDepositDate(o.eventDate) ??
        null,
      payorRaw: asStr(o.payorRaw) || null,
    });
  }
  return slots;
}

function parsePaySlots(
  raw: unknown,
  fallbackAmounts: number[],
  identifiedPaysRaw?: unknown,
): IncomeCalcPaySlot[] {
  const fromIdentified = parseIdentifiedPaysSlots(identifiedPaysRaw, fallbackAmounts);
  if (fromIdentified) {
    return fromIdentified;
  }

  if (Array.isArray(raw) && raw.length > 0) {
    const transactions = raw
      .map((row, index) => {
        if (!row || typeof row !== 'object') return null;
        const o = row as Record<string, unknown>;
        const amount = asNum(o.amount);
        if (amount == null) return null;
        const depositDate =
          parseDepositDate(o.depositDate) ??
          parseDepositDate(o.date) ??
          parseDepositDate(o.eventDate) ??
          null;
        return {
          index,
          amount,
          depositDate,
          payorRaw: asStr(o.payorRaw) || null,
        };
      })
      .filter((s): s is IncomeCalcPaySlot => s != null);

    return alignPaySlotsToAmounts(transactions, fallbackAmounts);
  }
  return fallbackAmounts.map((amount, index) => ({
    index,
    amount,
    depositDate: null,
    payorRaw: null,
  }));
}

function reconstructStreamFromLegacy(
  o: Record<string, unknown>,
  opts?: ParseCalcOptions,
): IncomeCalcStream | null {
  const monthly = asNum(o.monthlyAmountNormalized);
  if (monthly == null) return null;

  const trace = parseTrace(o.calculationValueTrace);
  const stabilityRaw =
    o.stabilityCvFromIqrAdjustedSeries &&
    typeof o.stabilityCvFromIqrAdjustedSeries === 'object' &&
    !Array.isArray(o.stabilityCvFromIqrAdjustedSeries)
      ? (o.stabilityCvFromIqrAdjustedSeries as Record<string, unknown>)
      : null;

  let rawPayAmounts =
    trace.length > 0
      ? trace.map((t) => t.originalPayAmount)
      : asNumberArray(
          Array.isArray(o.rawTransactions)
            ? (o.rawTransactions as Array<Record<string, unknown>>).map((t) => t.amount)
            : [],
        );

  let payAmountsUsed =
    asNumberArray(stabilityRaw?.payAmountsUsed).length > 0
      ? asNumberArray(stabilityRaw?.payAmountsUsed)
      : trace.length > 0
        ? trace.map((t) => t.valueUsedInCalculation)
        : [...rawPayAmounts];

  if (payAmountsUsed.length === 0 && rawPayAmounts.length === 0) return null;
  if (rawPayAmounts.length === 0) {
    rawPayAmounts = [...payAmountsUsed];
  }

  const highSpikeAmounts = asNumberArray(stabilityRaw?.highSpikeAmounts);
  const extraAmountRemoved = asNumberArray(o.extraAmountRemoved);

  const threshold = 1.5;
  const window = 3;
  const minLength = 3;
  const canSmooth = rawPayAmounts.length >= minLength;
  const smoothedValues = canSmooth ? rollingMedianSameLength(rawPayAmounts, window) : null;
  const boundsSource = smoothedValues ?? rawPayAmounts;
  const fence = computeIqrFence(boundsSource, threshold);

  let medianInBand: number | null = null;
  if (fence && rawPayAmounts.length > 0) {
    const inBand = rawPayAmounts.filter((v) => v >= fence.lowerBound && v <= fence.upperBound);
    medianInBand = inBand.length > 0 ? medianOf(inBand) : medianOf(rawPayAmounts);
  }

  if (trace.length > 0 && payAmountsUsed.length !== trace.length) {
    payAmountsUsed = trace.map((t) => t.valueUsedInCalculation);
  }

  const magnitudeCalculation = resolveMagnitudeFromSeries(
    payAmountsUsed.length > 0 ? payAmountsUsed : rawPayAmounts,
  );
  const frequency = normalizeFrequency(opts?.frequencyHint);
  const known = FREQUENCY_FACTORS[frequency];
  if (known && known.factor > 0) {
    const implied = monthly / known.factor;
    if (Math.abs(implied - magnitudeCalculation.magnitude) / Math.max(implied, 1) < 0.02) {
      magnitudeCalculation.magnitude = implied;
    }
  }

  const normalization = buildNormalization(monthly, magnitudeCalculation.magnitude, opts?.frequencyHint);

  return {
    payor: asStr(o.payor) || 'Unknown payor',
    payorType: asStr(o.payorType) || undefined,
    recurrenceEvents: asNum(o.recurrenceEvents) ?? undefined,
    monthlyAmountNormalized: monthly,
    reconstructed: true,
    paySlots: parsePaySlots(o.rawTransactions, rawPayAmounts, o.identifiedPays),
    calculationValueTrace:
      trace.length > 0
        ? trace
        : rawPayAmounts.map((originalPayAmount, i) => ({
            originalPayAmount,
            valueUsedInCalculation: payAmountsUsed[i] ?? originalPayAmount,
            replaced: (payAmountsUsed[i] ?? originalPayAmount) !== originalPayAmount,
            adjustmentKind:
              (payAmountsUsed[i] ?? originalPayAmount) !== originalPayAmount
                ? 'high_spike_adjusted'
                : 'none',
          })),
    iqrCalculation: {
      threshold,
      smoothing: {
        enabled: true,
        window,
        minLength,
        applied: Boolean(smoothedValues),
      },
      boundsFrom: smoothedValues ? 'smoothed' : 'raw',
      q1: fence?.q1 ?? null,
      q3: fence?.q3 ?? null,
      iqr: fence?.iqr ?? null,
      lowerBound: fence?.lowerBound ?? null,
      upperBound: fence?.upperBound ?? null,
      medianInBand,
      highSpikeAmounts,
      extraAmountRemoved,
      rawPayAmounts,
      smoothedValues,
      payAmountsUsed: payAmountsUsed.length > 0 ? payAmountsUsed : [...rawPayAmounts],
    },
    magnitudeCalculation,
    normalization,
  };
}

function parseStream(raw: unknown, opts?: ParseCalcOptions): IncomeCalcStream | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const iqrRaw =
    o.iqrCalculation && typeof o.iqrCalculation === 'object' && !Array.isArray(o.iqrCalculation)
      ? (o.iqrCalculation as Record<string, unknown>)
      : null;
  const magRaw =
    o.magnitudeCalculation && typeof o.magnitudeCalculation === 'object' && !Array.isArray(o.magnitudeCalculation)
      ? (o.magnitudeCalculation as Record<string, unknown>)
      : null;
  const normRaw =
    o.normalization && typeof o.normalization === 'object' && !Array.isArray(o.normalization)
      ? (o.normalization as Record<string, unknown>)
      : null;

  if (!iqrRaw || !magRaw || !normRaw) {
    return reconstructStreamFromLegacy(o, opts);
  }

  const monthly = asNum(o.monthlyAmountNormalized) ?? asNum(normRaw.monthlyAmount);
  if (monthly == null) return null;

  const smoothingRaw =
    iqrRaw.smoothing && typeof iqrRaw.smoothing === 'object' && !Array.isArray(iqrRaw.smoothing)
      ? (iqrRaw.smoothing as Record<string, unknown>)
      : null;

  const recencyRaw =
    magRaw.recency && typeof magRaw.recency === 'object' && !Array.isArray(magRaw.recency)
      ? (magRaw.recency as Record<string, unknown>)
      : null;

  const rawPayAmounts = asNumberArray(iqrRaw.rawPayAmounts);
  const payAmountsUsed = asNumberArray(iqrRaw.payAmountsUsed);
  let smoothedValues = asNumberArray(iqrRaw.smoothedValues);
  if (smoothedValues.length === 0) smoothedValues = [];

  const smoothing = smoothingRaw
    ? {
        enabled: Boolean(smoothingRaw.enabled),
        window: asNum(smoothingRaw.window) ?? 3,
        minLength: asNum(smoothingRaw.minLength) ?? 3,
        applied: Boolean(smoothingRaw.applied),
      }
    : null;

  if (
    smoothedValues.length === 0 &&
    smoothing?.applied &&
    rawPayAmounts.length > 0 &&
    smoothing.window > 1
  ) {
    smoothedValues = rollingMedianSameLength(rawPayAmounts, smoothing.window);
  }

  return {
    payor: asStr(o.payor) || 'Unknown payor',
    payorType: asStr(o.payorType) || undefined,
    recurrenceEvents: asNum(o.recurrenceEvents) ?? undefined,
    monthlyAmountNormalized: monthly,
    reconstructed: false,
    paySlots: parsePaySlots(o.rawTransactions, rawPayAmounts, o.identifiedPays),
    calculationValueTrace: parseTrace(o.calculationValueTrace),
    iqrCalculation: {
      threshold: asNum(iqrRaw.threshold),
      smoothing,
      boundsFrom: (asStr(iqrRaw.boundsFrom) as 'raw' | 'smoothed') || null,
      q1: asNum(iqrRaw.q1),
      q3: asNum(iqrRaw.q3),
      iqr: asNum(iqrRaw.iqr),
      lowerBound: asNum(iqrRaw.lowerBound),
      upperBound: asNum(iqrRaw.upperBound),
      medianInBand: asNum(iqrRaw.medianInBand),
      highSpikeAmounts: asNumberArray(iqrRaw.highSpikeAmounts),
      extraAmountRemoved: asNumberArray(iqrRaw.extraAmountRemoved),
      rawPayAmounts,
      smoothedValues: smoothedValues.length > 0 ? smoothedValues : null,
      payAmountsUsed,
    },
    magnitudeCalculation: {
      magnitudeMethod: asStr(magRaw.magnitudeMethod) || 'mean',
      magnitude: asNum(magRaw.magnitude) ?? 0,
      modeValue: asNum(magRaw.modeValue) ?? 0,
      modeRatio: asNum(magRaw.modeRatio) ?? 0,
      seriesMean: asNum(magRaw.seriesMean) ?? 0,
      recency: recencyRaw
        ? {
            evaluated: Boolean(recencyRaw.evaluated),
            lastThreeMean: asNum(recencyRaw.lastThreeMean),
            recentModeRatio: asNum(recencyRaw.recentModeRatio),
            hikeGatePassed: Boolean(recencyRaw.hikeGatePassed),
          }
        : null,
      rulePath: asStr(magRaw.rulePath) || '',
    },
    normalization: {
      incomeClass: asStr(normRaw.incomeClass) || undefined,
      frequency: asStr(normRaw.frequency) || normalizeFrequency(opts?.frequencyHint) || 'UNKNOWN',
      basis: asStr(normRaw.basis) || undefined,
      basisAmount: asNum(normRaw.basisAmount) ?? 0,
      monthlyFactor: asNum(normRaw.monthlyFactor) ?? 1,
      monthlyAmount: asNum(normRaw.monthlyAmount) ?? monthly,
      formula: asStr(normRaw.formula) || '',
      daysInMonth: asNum(normRaw.daysInMonth) ?? undefined,
      averageGapDays: asNum(normRaw.averageGapDays) ?? undefined,
      effectiveGapDays: asNum(normRaw.effectiveGapDays) ?? undefined,
    },
  };
}

export function parseCalculationTransactions(
  payload: unknown,
  opts?: ParseCalcOptions,
): IncomeCalculationTransactions | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const o = payload as Record<string, unknown>;
  const streamsRaw = Array.isArray(o.streams) ? o.streams : [];
  const streams = streamsRaw
    .map((row) => parseStream(row, opts))
    .filter((s): s is IncomeCalcStream => s != null);
  return {
    schemaVersion: asNum(o.schemaVersion) ?? undefined,
    capturedAt: asStr(o.capturedAt) || undefined,
    bankVerificationId: asStr(o.bankVerificationId) || undefined,
    note: asStr(o.note) || undefined,
    depositRollup:
      o.depositRollup && typeof o.depositRollup === 'object' && !Array.isArray(o.depositRollup)
        ? {
            totalIngestRows: asNum((o.depositRollup as Record<string, unknown>).totalIngestRows) ?? 0,
            eligibleCount: asNum((o.depositRollup as Record<string, unknown>).eligibleCount) ?? 0,
            ineligibleCount: asNum((o.depositRollup as Record<string, unknown>).ineligibleCount) ?? 0,
          }
        : undefined,
    streams,
  };
}

export function pickPrimaryCalcStream(
  audit: IncomeCalculationTransactions,
  primaryPayor?: string | null,
): IncomeCalcStream | null {
  const streams = audit.streams ?? [];
  if (streams.length === 0) return null;
  const key = (primaryPayor ?? '').trim().toLowerCase();
  if (key) {
    const hit = streams.find((s) => s.payor.trim().toLowerCase() === key);
    if (hit) return hit;
  }
  return [...streams].sort((a, b) => b.monthlyAmountNormalized - a.monthlyAmountNormalized)[0] ?? null;
}

export function buildDerivationSteps(stream: IncomeCalcStream): DerivationStep[] {
  const iqr = stream.iqrCalculation;
  const mag = stream.magnitudeCalculation;
  const norm = stream.normalization;
  const raw = iqr.rawPayAmounts;
  const smoothingApplied = Boolean(iqr.smoothing?.applied && iqr.smoothedValues?.length);
  const hasIqr = iqr.q1 != null && iqr.q3 != null && iqr.iqr != null;
  const replacedCount = stream.calculationValueTrace.filter((t) => t.replaced).length;

  return [
    {
      id: 'identified',
      label: 'Identified pays',
      shortResult: `${raw.length || iqr.payAmountsUsed.length} amounts`,
    },
    {
      id: 'rolling_median',
      label: 'Rolling median',
      shortResult: smoothingApplied ? `window ${iqr.smoothing?.window ?? 3}` : 'Not applied',
      skipped: !smoothingApplied,
      skipReason: !smoothingApplied
        ? raw.length <= (iqr.smoothing?.minLength ?? 3)
          ? `Skipped when fewer than ${iqr.smoothing?.minLength ?? 3} pays`
          : 'Smoothing was off for this run'
        : undefined,
    },
    {
      id: 'quantiles',
      label: 'Q1 · Q3 · IQR',
      shortResult: hasIqr ? `${money(iqr.q1, 0)} → ${money(iqr.q3, 0)}` : 'Too few pays',
      skipped: !hasIqr,
      skipReason: !hasIqr ? 'IQR needs more than 2 pays' : undefined,
    },
    {
      id: 'fence',
      label: 'Allowed range',
      shortResult:
        iqr.lowerBound != null && iqr.upperBound != null
          ? `${money(iqr.lowerBound, 0)} – ${money(iqr.upperBound, 0)}`
          : '—',
      skipped: iqr.lowerBound == null || iqr.upperBound == null,
    },
    {
      id: 'adjusted',
      label: 'Spike adjust',
      shortResult: replacedCount > 0 ? `${replacedCount} capped` : 'No changes',
    },
    {
      id: 'magnitude',
      label: 'Period amount',
      shortResult: `${mag.magnitudeMethod} · ${money(mag.magnitude, 0)}`,
    },
    {
      id: 'normalize',
      label: 'Monthly',
      shortResult: money(norm.monthlyAmount, 0),
    },
  ];
}

export function buildDerivationViewModel(
  audit: IncomeCalculationTransactions | null,
  primaryPayor?: string | null,
): DerivationViewModel | null {
  if (!audit) return null;
  const stream = pickPrimaryCalcStream(audit, primaryPayor);
  if (!stream) return null;
  return {
    payor: stream.payor,
    monthlyAmount: stream.monthlyAmountNormalized,
    steps: buildDerivationSteps(stream),
    stream,
  };
}

export function extractCalculationTransactionsFromDetail(
  detail: unknown,
): IncomeCalculationTransactions | null {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  const o = detail as Record<string, unknown>;
  const metrics =
    o.metrics && typeof o.metrics === 'object' && !Array.isArray(o.metrics)
      ? (o.metrics as Record<string, unknown>)
      : null;
  const frequencyHint =
    typeof metrics?.frequencyClassification === 'string' ? metrics.frequencyClassification : null;
  return parseCalculationTransactions(o.calculationTransactions, { frequencyHint });
}
